import { useState, useEffect } from 'react';

const ROLES = ["Senior BDM", "Sales Builder", "Part-time Engineer"];

export default function App() {
  const [roleIdx, setRoleIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const iv = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setRoleIdx(i => (i + 1) % ROLES.length); setVisible(true); }, 350);
    }, 2600);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ fontFamily: 'var(--body)', background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=Lora:ital,wght@0,400;1,400&family=DM+Mono&display=swap');

        :root {
          --bg: #f5f0e8;
          --bg2: #eee8d8;
          --text: #1c1c1c;
          --muted: #7a7060;
          --accent: #c8440a;
          --accent2: #e8913a;
          --green: #1a3a1a;
          --card: #ffffff;
          --border: #e0d8c8;
          --display: 'Syne', sans-serif;
          --body: 'Lora', serif;
          --mono: 'DM Mono', monospace;
        }

        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        /* NAV */
        nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          padding: 18px 48px;
          display: flex; align-items: center; justify-content: space-between;
          transition: all 0.3s;
        }
        nav.scrolled {
          background: rgba(245,240,232,0.92);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border);
          padding: 14px 48px;
        }
        .nav-logo { font-family: var(--display); font-size: 18px; font-weight: 800; color: var(--text); letter-spacing: -0.02em; text-decoration: none; }
        .nav-links { display: flex; gap: 32px; align-items: center; }
        .nav-links a { font-family: var(--mono); font-size: 11px; color: var(--muted); text-decoration: none; letter-spacing: 0.08em; text-transform: uppercase; transition: color 0.2s; }
        .nav-links a:hover { color: var(--accent); }
        .nav-cta { background: var(--accent); color: #fff !important; padding: 8px 18px !important; border-radius: 6px; font-weight: 500; }
        .nav-cta:hover { background: #a83508 !important; }

        /* HERO */
        .hero {
          min-height: 100vh;
          display: flex; flex-direction: column; justify-content: center;
          padding: 120px 48px 80px;
          max-width: 1100px; margin: 0 auto;
          position: relative;
        }
        .hero-tag {
          font-family: var(--mono); font-size: 11px; color: var(--accent);
          letter-spacing: 0.15em; text-transform: uppercase;
          margin-bottom: 20px;
          display: flex; align-items: center; gap: 10px;
        }
        .hero-tag::before { content: ''; display: block; width: 28px; height: 2px; background: var(--accent); }
        .hero-name {
          font-family: var(--display); font-size: clamp(52px, 8vw, 96px);
          font-weight: 800; color: var(--text); line-height: 0.95;
          letter-spacing: -0.03em; margin-bottom: 24px;
        }
        .hero-name span { color: var(--accent); }
        .hero-role {
          font-family: var(--display); font-size: clamp(20px, 3vw, 28px);
          font-weight: 400; color: var(--muted);
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 36px;
        }
        .role-pill {
          background: var(--green); color: #a8d878;
          padding: 4px 14px; border-radius: 20px;
          font-family: var(--mono); font-size: 12px;
          transition: opacity 0.3s;
        }
        .hero-desc {
          font-family: var(--body); font-size: 17px; color: var(--muted);
          max-width: 520px; line-height: 1.75;
          margin-bottom: 48px;
          font-style: italic;
        }
        .hero-actions { display: flex; gap: 14px; flex-wrap: wrap; }
        .btn-primary {
          background: var(--accent); color: #fff;
          padding: 14px 28px; border-radius: 8px; border: none;
          font-family: var(--display); font-size: 14px; font-weight: 700;
          letter-spacing: 0.06em; cursor: pointer;
          text-decoration: none; display: inline-block;
          transition: all 0.2s;
          box-shadow: 0 4px 16px rgba(200,68,10,0.3);
        }
        .btn-primary:hover { background: #a83508; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(200,68,10,0.4); }
        .btn-secondary {
          background: transparent; color: var(--text);
          padding: 13px 28px; border-radius: 8px; border: 2px solid var(--border);
          font-family: var(--display); font-size: 14px; font-weight: 700;
          letter-spacing: 0.06em; cursor: pointer;
          text-decoration: none; display: inline-block;
          transition: all 0.2s;
        }
        .btn-secondary:hover { border-color: var(--accent); color: var(--accent); }
        .hero-nums {
          position: absolute; right: 48px; bottom: 80px;
          display: flex; flex-direction: column; gap: 24px; align-items: flex-end;
        }
        .hero-stat { text-align: right; }
        .hero-stat-n { font-family: var(--display); font-size: 36px; font-weight: 800; color: var(--accent); line-height: 1; }
        .hero-stat-l { font-family: var(--mono); font-size: 10px; color: var(--muted); letter-spacing: 0.1em; text-transform: uppercase; margin-top: 2px; }

        /* DIVIDER */
        .divider { height: 1px; background: var(--border); max-width: 1100px; margin: 0 auto; }

        /* SECTION */
        section { max-width: 1100px; margin: 0 auto; padding: 88px 48px; }
        .section-label {
          font-family: var(--mono); font-size: 10px; color: var(--accent);
          letter-spacing: 0.2em; text-transform: uppercase;
          margin-bottom: 14px; display: flex; align-items: center; gap: 8px;
        }
        .section-label::before { content: ''; display: block; width: 20px; height: 1.5px; background: var(--accent); }
        .section-title {
          font-family: var(--display); font-size: clamp(30px, 4vw, 44px);
          font-weight: 800; color: var(--text); letter-spacing: -0.02em;
          margin-bottom: 48px; line-height: 1.1;
        }

        /* EXPERIENCE */
        .exp-item {
          display: grid; grid-template-columns: 200px 1fr;
          gap: 32px; padding: 36px 0;
          border-top: 1px solid var(--border);
        }
        .exp-item:last-child { border-bottom: 1px solid var(--border); }
        .exp-date { font-family: var(--mono); font-size: 11px; color: var(--muted); letter-spacing: 0.06em; padding-top: 4px; }
        .exp-role { font-family: var(--display); font-size: 20px; font-weight: 700; color: var(--text); margin-bottom: 4px; letter-spacing: -0.01em; }
        .exp-company { font-family: var(--mono); font-size: 11px; color: var(--accent); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 14px; }
        .exp-bullets { list-style: none; display: flex; flex-direction: column; gap: 8px; }
        .exp-bullets li { font-family: var(--body); font-size: 14px; color: var(--muted); line-height: 1.65; padding-left: 16px; position: relative; }
        .exp-bullets li::before { content: '—'; position: absolute; left: 0; color: var(--accent2); font-size: 12px; }

        /* TOOLS SECTION */
        .tools-card {
          background: var(--green);
          border-radius: 20px;
          padding: 48px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 32px; flex-wrap: wrap;
          position: relative; overflow: hidden;
        }
        .tools-card::before {
          content: '⚙';
          position: absolute; right: -20px; top: -30px;
          font-size: 180px; opacity: 0.04; line-height: 1;
          pointer-events: none;
        }
        .tools-card-left { flex: 1; min-width: 260px; }
        .tools-card-tag { font-family: var(--mono); font-size: 10px; color: #5a8a42; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 12px; }
        .tools-card-title { font-family: var(--display); font-size: 32px; font-weight: 800; color: #a8d878; letter-spacing: -0.01em; line-height: 1.1; margin-bottom: 14px; }
        .tools-card-desc { font-family: var(--body); font-size: 14px; color: #6a9a52; line-height: 1.7; font-style: italic; }
        .tool-chip {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
          color: #c8e8a8; padding: 10px 16px; border-radius: 10px;
          font-family: var(--mono); font-size: 11px;
          cursor: pointer; text-decoration: none;
          transition: all 0.2s;
        }
        .tool-chip:hover { background: rgba(245,166,35,0.15); border-color: rgba(245,166,35,0.4); color: #f5a623; transform: translateY(-2px); }
        .tool-chips { display: flex; flex-direction: column; gap: 10px; }

        /* SKILLS */
        .skills-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; }
        .skill-card {
          background: var(--card); border: 1px solid var(--border);
          border-radius: 14px; padding: 24px;
          transition: all 0.2s;
        }
        .skill-card:hover { border-color: var(--accent2); transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
        .skill-card-icon { font-size: 24px; margin-bottom: 12px; }
        .skill-card-title { font-family: var(--display); font-size: 15px; font-weight: 700; color: var(--text); margin-bottom: 10px; }
        .skill-tags { display: flex; flex-wrap: wrap; gap: 6px; }
        .skill-tag { font-family: var(--mono); font-size: 10px; color: var(--muted); background: var(--bg2); padding: 3px 9px; border-radius: 4px; letter-spacing: 0.04em; }

        /* EDUCATION */
        .edu-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .edu-card { background: var(--card); border: 1px solid var(--border); border-radius: 14px; padding: 28px; }
        .edu-deg { font-family: var(--display); font-size: 17px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
        .edu-school { font-family: var(--mono); font-size: 11px; color: var(--accent); letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 6px; }
        .edu-year { font-family: var(--mono); font-size: 11px; color: var(--muted); }

        /* CONTACT */
        .contact-wrap {
          background: var(--text); border-radius: 20px;
          padding: 64px 48px; text-align: center;
        }
        .contact-title { font-family: var(--display); font-size: clamp(28px, 4vw, 48px); font-weight: 800; color: #f5f0e8; letter-spacing: -0.02em; margin-bottom: 16px; }
        .contact-sub { font-family: var(--body); font-size: 16px; color: #7a7060; font-style: italic; margin-bottom: 36px; }
        .contact-links { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }
        .contact-link {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 24px; border-radius: 8px; text-decoration: none;
          font-family: var(--display); font-size: 13px; font-weight: 700;
          letter-spacing: 0.06em; transition: all 0.2s;
        }
        .contact-link-li { background: #0a66c2; color: #fff; }
        .contact-link-li:hover { background: #085fad; transform: translateY(-2px); }
        .contact-link-mail { background: rgba(255,255,255,0.08); color: #c8c0b0; border: 1px solid rgba(255,255,255,0.12); }
        .contact-link-mail:hover { border-color: var(--accent2); color: var(--accent2); }

        /* FOOTER */
        footer { text-align: center; padding: 32px 48px; }
        footer p { font-family: var(--mono); font-size: 11px; color: var(--muted); letter-spacing: 0.06em; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.6s ease both; }
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.22s; }
        .delay-3 { animation-delay: 0.36s; }
        .delay-4 { animation-delay: 0.5s; }
        .delay-5 { animation-delay: 0.64s; }

        @media (max-width: 768px) {
          nav { padding: 16px 24px; }
          nav.scrolled { padding: 12px 24px; }
          .nav-links { display: none; }
          .hero { padding: 100px 24px 60px; }
          .hero-nums { display: none; }
          section { padding: 60px 24px; }
          .exp-item { grid-template-columns: 1fr; gap: 8px; }
          .edu-grid { grid-template-columns: 1fr; }
          .tools-card { padding: 32px 24px; }
          .contact-wrap { padding: 40px 24px; }
        }
      `}</style>

      {/* NAV */}
      <nav className={scrolled ? 'scrolled' : ''}>
        <a href="#top" className="nav-logo">NR.</a>
        <div className="nav-links">
          <a href="#experience">Experience</a>
          <a href="#tools">Tools</a>
          <a href="#skills">Skills</a>
          <a href="#contact" className="nav-cta">Say Hello</a>
        </div>
      </nav>

      {/* HERO */}
      <div id="top" className="hero">
        <div className="hero-tag fade-up">Available for opportunities</div>
        <h1 className="hero-name fade-up delay-1">
          Nilargha<br /><span>Roy.</span>
        </h1>
        <div className="hero-role fade-up delay-2">
          <span className="role-pill" style={{ opacity: visible ? 1 : 0 }}>{ROLES[roleIdx]}</span>
          <span>@ Scaler / Interviewbit</span>
        </div>
        <p className="hero-desc fade-up delay-3">
          Senior BDM who builds the tools his team actually uses.
          From sales playbooks to internal CRMs — if it removes friction, I'll build it.
        </p>
        <div className="hero-actions fade-up delay-4">
          <a href="#experience" className="btn-primary">See My Work</a>
          <a href="https://tools.nilargha.work" className="btn-secondary" target="_blank" rel="noreferrer">🛠 tools.nilargha.work</a>
        </div>
        <div className="hero-nums fade-up delay-5">
          <div className="hero-stat">
            <div className="hero-stat-n">3+</div>
            <div className="hero-stat-l">Years Leading Teams</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-n">40%</div>
            <div className="hero-stat-l">YoY Revenue Growth</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-n">120%+</div>
            <div className="hero-stat-l">Consistent Target Hit</div>
          </div>
        </div>
      </div>

      <div className="divider" />

      {/* EXPERIENCE */}
      <section id="experience">
        <div className="section-label">Career</div>
        <h2 className="section-title">Where I've<br />made impact.</h2>

        <div className="exp-item">
          <div>
            <div className="exp-date">Feb 2024 — Present</div>
          </div>
          <div>
            <div className="exp-role">Assistant Senior Manager, Business Development</div>
            <div className="exp-company">Interviewbit / Scaler · Noida & Bangalore</div>
            <ul className="exp-bullets">
              <li>Led a 15-member sales team — coaching on consultative selling, objection handling, and value-based conversations</li>
              <li>Played founding role in establishing and scaling the Noida office; defined initial GTM structure and sales motion</li>
              <li>Surpassed quarterly revenue targets consistently; implemented sales cadences improving lead-to-close rate</li>
              <li>Partnered with Product to build and roll out a smart internal CRM, adopted org-wide — replacing Leadsquared</li>
              <li>Influenced the creation of AI-powered lead summaries and an AI-driven sales roleplay practice tool</li>
              <li>Championed adoption of Outreach and Apollo.io for improved sales execution</li>
            </ul>
          </div>
        </div>

        <div className="exp-item">
          <div>
            <div className="exp-date">Mar 2022 — Feb 2024</div>
          </div>
          <div>
            <div className="exp-role">Business Development Manager</div>
            <div className="exp-company">Interviewbit / Scaler · Bangalore</div>
            <ul className="exp-bullets">
              <li>Drove outbound and inbound strategy for an online tech skilling SaaS platform; enabled 40% YoY revenue growth</li>
              <li>Designed and executed structured sales playbooks using MEDDIC and SPIN frameworks</li>
              <li>Developed talent pipelines and mentored BDRs; improved team ramp-up time by 30%</li>
              <li>Partnered with Product & Marketing to refine ICPs and increase demo booking rates by 35%</li>
            </ul>
          </div>
        </div>

        <div className="exp-item">
          <div>
            <div className="exp-date">Aug 2021 — Mar 2022</div>
          </div>
          <div>
            <div className="exp-role">Business Development Representative</div>
            <div className="exp-company">Interviewbit / Scaler · Bangalore</div>
            <ul className="exp-bullets">
              <li>Managed full sales cycle from discovery to close with mid-sized and enterprise customers in India & APAC</li>
              <li>Improved MQL to SQL conversion by 33% via data-driven prospecting and cadence testing</li>
              <li>Consistently hit 120%+ of targets through outbound prospecting, tailored demo pitches, and follow-up</li>
            </ul>
          </div>
        </div>

        <div className="exp-item">
          <div>
            <div className="exp-date">Feb 2021 — Jun 2021</div>
          </div>
          <div>
            <div className="exp-role">Software Engineer</div>
            <div className="exp-company">Opentext · Bangalore</div>
            <ul className="exp-bullets">
              <li>Built cloud-native microservices for OT2 platform using Java, Spring Boot, and Selenium</li>
              <li>Developed internal tools to accelerate QA processes; collaborated with DevOps and architecture teams</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* TOOLS */}
      <section id="tools">
        <div className="section-label">Side Projects</div>
        <h2 className="section-title">Tools I've<br />built.</h2>
        <div className="tools-card">
          <div className="tools-card-left">
            <div className="tools-card-tag">🛠 tools.nilargha.work</div>
            <div className="tools-card-title">The Toolbox</div>
            <div className="tools-card-desc">
              Internal tools built to cut manual work for sales teams.
              Paste your pivot data, get a live scoreboard. More being added.
            </div>
          </div>
          <div className="tool-chips">
            <a href="https://tools.nilargha.work/reportgen" className="tool-chip" target="_blank" rel="noreferrer">
              📊 <span>Report Generator</span> <span style={{opacity:0.4}}>→</span>
            </a>
            <a href="https://tools.nilargha.work/incentives" className="tool-chip" target="_blank" rel="noreferrer">
              💰 <span>Incentive Calculator</span> <span style={{opacity:0.4}}>→</span>
            </a>
            <a href="https://tools.nilargha.work" className="tool-chip" target="_blank" rel="noreferrer" style={{borderStyle:'dashed', opacity:0.5}}>
              🔧 <span>More coming soon</span>
            </a>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* SKILLS */}
      <section id="skills">
        <div className="section-label">Capabilities</div>
        <h2 className="section-title">What I bring<br />to the table.</h2>
        <div className="skills-grid">
          <div className="skill-card">
            <div className="skill-card-icon">🎯</div>
            <div className="skill-card-title">Sales Methodology</div>
            <div className="skill-tags">
              {["MEDDIC","BANT","Sandler","SPIN","Consultative Selling","Value-Based Selling"].map(s => <span key={s} className="skill-tag">{s}</span>)}
            </div>
          </div>
          <div className="skill-card">
            <div className="skill-card-icon">⚙️</div>
            <div className="skill-card-title">Sales Execution</div>
            <div className="skill-tags">
              {["Cold Calling","Cold Emailing","Prospecting","Upsell / Cross-sell","Account Planning","Demo Pitching"].map(s => <span key={s} className="skill-tag">{s}</span>)}
            </div>
          </div>
          <div className="skill-card">
            <div className="skill-card-icon">🛠</div>
            <div className="skill-card-title">Sales Tools</div>
            <div className="skill-tags">
              {["Apollo.io","Outreach","Salesforce","LinkedIn Sales Nav","Leadsquared","ZoomInfo","SQL"].map(s => <span key={s} className="skill-tag">{s}</span>)}
            </div>
          </div>
          <div className="skill-card">
            <div className="skill-card-icon">💻</div>
            <div className="skill-card-title">Engineering</div>
            <div className="skill-tags">
              {["React","JavaScript","Java","Spring Boot","SQL","Microservices","REST APIs"].map(s => <span key={s} className="skill-tag">{s}</span>)}
            </div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* EDUCATION */}
      <section>
        <div className="section-label">Education</div>
        <h2 className="section-title">Academic<br />background.</h2>
        <div className="edu-grid">
          <div className="edu-card">
            <div className="edu-deg">MS in Computer Science</div>
            <div className="edu-school">Woolf University · Online</div>
            <div className="edu-year">Mar 2025 – Sep 2026</div>
          </div>
          <div className="edu-card">
            <div className="edu-deg">B.Tech in Computer Science</div>
            <div className="edu-school">SRM University · Chennai</div>
            <div className="edu-year">Jul 2017 – May 2021</div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* CONTACT */}
      <section id="contact">
        <div className="contact-wrap">
          <div className="contact-title">Let's connect.</div>
          <div className="contact-sub">Open to interesting opportunities, collabs, or just a good conversation about sales & building.</div>
          <div className="contact-links">
            <a href="https://www.linkedin.com/in/nilargha/" className="contact-link contact-link-li" target="_blank" rel="noreferrer">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
            <a href="mailto:nilarghar@gmail.com" className="contact-link contact-link-mail">
              nilarghar@gmail.com
            </a>
          </div>
        </div>
      </section>

      <footer>
        <p>© 2025 NILARGHA ROY · <a href="https://tools.nilargha.work" style={{color:'inherit', textDecoration:'none'}}>tools.nilargha.work</a></p>
      </footer>
    </div>
  );
}
